import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Select,
  Checkbox,
  BlockStack,
  Text,
} from '@shopify/polaris';
import { useState, useEffect } from 'react';
import { createApiUrl } from '../config/api';
import { useApiClient } from '../hooks/useApiClient';
import { useAppNavigate } from '../hooks/useAppNavigate';

interface SettingsData {
  enableAnimations: boolean;
  animationDuration: number;
  lazyLoad: boolean;
  cacheLabels: boolean;
  maxLabelsPerProduct: number;
  defaultPosition: string;
}

export default function Settings() {
  const navigate = useAppNavigate();
  const api = useApiClient();
  const [settings, setSettings] = useState<SettingsData>({
    enableAnimations: true,
    animationDuration: 300,
    lazyLoad: true,
    cacheLabels: true,
    maxLabelsPerProduct: 3,
    defaultPosition: 'top-left',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await api.get<SettingsData>(createApiUrl('api/settings'));
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      alert(`Error fetching settings: ${(error as any)?.message || 'Unknown error'}`);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.put(createApiUrl('api/settings'), settings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert(`Error saving settings: ${(error as any)?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: string, value: any) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Page
      title="Settings"
      backAction={{ onAction: () => navigate('/') }}
      primaryAction={{
        content: 'Save Settings',
        onAction: handleSubmit,
        loading,
      }}
    >
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                General Settings
              </Text>
              <FormLayout>
                <Checkbox
                  label="Enable Animations"
                  checked={settings.enableAnimations}
                  onChange={(value) => updateField('enableAnimations', value)}
                  helpText="Enable animations for labels"
                />
                <TextField
                  label="Animation Duration (ms)"
                  type="number"
                  value={settings.animationDuration.toString()}
                  onChange={(value) => updateField('animationDuration', parseInt(value))}
                  autoComplete="off"
                />
                <Checkbox
                  label="Lazy Load Labels"
                  checked={settings.lazyLoad}
                  onChange={(value) => updateField('lazyLoad', value)}
                  helpText="Load labels only when they become visible"
                />
                <Checkbox
                  label="Cache Labels"
                  checked={settings.cacheLabels}
                  onChange={(value) => updateField('cacheLabels', value)}
                  helpText="Cache labels for better performance"
                />
                <TextField
                  label="Max Labels Per Product"
                  type="number"
                  value={settings.maxLabelsPerProduct.toString()}
                  onChange={(value) => updateField('maxLabelsPerProduct', parseInt(value))}
                  autoComplete="off"
                />
                <Select
                  label="Default Position"
                  options={[
                    { label: 'Top Left', value: 'top-left' },
                    { label: 'Top Right', value: 'top-right' },
                    { label: 'Bottom Left', value: 'bottom-left' },
                    { label: 'Bottom Right', value: 'bottom-right' },
                    { label: 'Center', value: 'center' },
                  ]}
                  value={settings.defaultPosition}
                  onChange={(value) => updateField('defaultPosition', value)}
                />
              </FormLayout>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                About
              </Text>
              <Text as="p" variant="bodyMd">
                Product Labels & Badges App v1.0.0
              </Text>
              <Text as="p" variant="bodyMd">
                Add customizable labels and badges to your products.
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
